import { useState, useEffect } from "react";
import axios from "axios";
import { X, AlertTriangle, Check, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { J, MANROPE, TRACK_TAG, ddMon, dueLine, warningsFor } from "./journeyTokens";
import { StageBar, PayingTag } from "./JourneyRow";
import { JourneyHoursDialog } from "./JourneyHoursDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABEL = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", color: J.ink2 };
const INPUT = "w-full h-9 rounded-[7px] px-2.5 text-[13px] mt-1 bg-white";

/* Legacy steps were ticked before completed_date existed — fall back to the stamp. */
const doneOn = (s) => (s.completed_date || s.completed_at || s.done_at || "").slice(0, 10);

/* §10 — the side panel. Every field saves on change; steps sync to To-dos. */
export const JourneyPanel = ({ r, today, csmOptions, onClose, onSaved, onError }) => {
  const [steps, setSteps] = useState([]);
  const [openStage, setOpenStage] = useState(null);
  const [hours, setHours] = useState("");
  const [busy, setBusy] = useState(null);
  const [loggedHours, setLoggedHours] = useState(null);   // fresh figure straight from the POST
  const [hoursOpen, setHoursOpen] = useState(false);

  useEffect(() => {
    setLoggedHours(null);
    setOpenStage((r.stages || [])[r.stage_index - 1]?.key || null);
    axios.get(`${API}/customer-success/journeys/${r.client_id}/actions`)
      .then((x) => setSteps(x.data.actions || []))
      .catch(() => onError("Could not load the steps"));
  }, [r.client_id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch, label) => {
    setBusy(label);
    try { await axios.put(`${API}/customer-success/journeys/${r.client_id}`, patch); onSaved(); }
    catch (e) { onError(e?.response?.data?.detail || `Could not save ${label}`); }
    finally { setBusy(null); }
  };

  const toggleStep = async (s) => {
    const done = s.status === "done";
    setSteps((ls) => ls.map((x) => (x.id === s.id
      ? { ...x, status: done ? "open" : "done", completed_date: done ? null : today }
      : x)));
    try {
      await axios.post(`${API}/customer-success/journeys/actions/${s.id}/${done ? "reopen" : "complete"}`,
        done ? undefined : { hours: 0 });
      onSaved();
      const x = await axios.get(`${API}/customer-success/journeys/${r.client_id}/actions`);
      setSteps(x.data.actions || []);
    } catch (e) {
      setSteps((ls) => ls.map((x) => (x.id === s.id
        ? { ...x, status: s.status, completed_date: s.completed_date || null } : x)));
      onError(e?.response?.data?.detail || "Could not update that step");
    }
  };

  const setStepDate = async (s, value) => {
    if (!value) return;
    setSteps((ls) => ls.map((x) => (x.id === s.id ? { ...x, completed_date: value } : x)));
    try {
      await axios.put(`${API}/customer-success/journeys/actions/${s.id}/completed-date`,
        { completed_on: value });
    } catch (e) {
      setSteps((ls) => ls.map((x) => (x.id === s.id ? { ...x, completed_date: s.completed_date } : x)));
      onError(e?.response?.data?.detail || "Could not change that date");
    }
  };

  const removeStep = async (s) => {
    setBusy(`rm-${s.id}`);
    try {
      await axios.delete(`${API}/customer-success/journeys/actions/${s.id}`);
      await onSaved();
      const x = await axios.get(`${API}/customer-success/journeys/${r.client_id}/actions`);
      setSteps(x.data.actions || []);
    } catch (e) {
      onError(e?.response?.data?.detail || "Could not remove that step");
    } finally { setBusy(null); }
  };

  const tickStage = async (st, completed) => {
    setBusy(`stage-${st.key}`);
    try {
      await axios.post(
        `${API}/customer-success/journeys/${r.client_id}/stages/${st.key}/tick`, { completed });
      await onSaved();
      const x = await axios.get(`${API}/customer-success/journeys/${r.client_id}/actions`);
      setSteps(x.data.actions || []);
    } catch (e) {
      const d = e?.response?.data?.detail;
      onError(typeof d === "string" ? d : d?.message || "Could not change that stage");
    } finally { setBusy(null); }
  };

  const logHours = async () => {
    const v = parseFloat(hours);
    if (!v || v <= 0) return;
    setBusy("hours");
    try {
      const res = await axios.post(`${API}/customer-success/journeys/${r.client_id}/hours`, { hours: v });
      setLoggedHours(res.data?.implementation_hours ?? null);
      setHours(""); await onSaved();
    } catch (e) { onError(e?.response?.data?.detail || "Could not log those hours"); }
    finally { setBusy(null); }
  };

  const tag = TRACK_TAG[r.track] || TRACK_TAG.GUIDED;
  const shownHours = Math.max(loggedHours ?? 0, r.hours_logged || 0);
  const warns = warningsFor({ ...r, hours_over: !!r.hours_allowance && shownHours > r.hours_allowance }, today);
  const byStage = (key) => steps.filter((s) => s.stage_key === key);

  return (
    <div className="fixed inset-0 z-[70]" data-testid="journey-panel">
      {hoursOpen && (
        <JourneyHoursDialog clientId={r.client_id} companyName={r.company_name}
          onClose={() => setHoursOpen(false)} onError={onError}
          onSaved={async () => { setLoggedHours(null); await onSaved(); }} />
      )}
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.28)" }} onClick={onClose} />
      <div className="absolute top-0 right-0 h-full flex flex-col"
        style={{ width: "min(560px, 100vw)", background: J.head }}>

        <div className="px-6 py-4 bg-white" style={{ borderBottom: `1px solid ${J.rule}` }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate" style={{ ...MANROPE, fontSize: 20, fontWeight: 800, color: J.ink }}>{r.company_name}</span>
                <span className="mono shrink-0" style={{ fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "2px 6px",
                  background: tag.fill, color: tag.ink, border: `1px solid ${tag.edge}` }}>{tag.label}</span>
                <PayingTag billing={r.billing} clientId={r.client_id} />
              </div>
              {r.billing?.paying && (
                <div className="text-[11.5px] mt-1" style={{ color: J.ink2 }}
                     data-testid="journey-panel-billing">
                  {[
                    r.billing.plans?.length ? r.billing.plans.join(" + ") : "Subscription",
                    r.billing.amount_text
                      || (r.billing.mrr ? `A$${(r.billing.mrr / 100).toFixed(0)} per month` : null),
                    r.billing.trialing ? "on trial" : null,
                    r.billing.renews_on ? `renews ${r.billing.renews_on.slice(0, 10)}` : null,
                    r.billing.ending ? "cancels at period end" : null,
                  ].filter(Boolean).join(" · ")}
                </div>
              )}
              <div className="mono text-[11px] mt-0.5" style={{ color: J.muted }}>
                {r.client_id} · Stage {r.stage_index} of {r.stages_total} · {r.stage_name}
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-[7px] flex items-center justify-center hover:bg-[#F5F2EA]"
              style={{ color: J.ink2 }} data-testid="journey-panel-close"><X className="w-4 h-4" /></button>
          </div>

          <div className="mt-3"><StageBar stages={r.stages} currentIndex={r.stage_index} height={8} /></div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <div style={LABEL}>CSM</div>
              <select defaultValue={r.csm_owner_id || ""} onChange={(e) => save({ csm_owner_id: e.target.value || null }, "the CSM")}
                className={INPUT} data-testid="journey-panel-csm"
                style={{ border: `1px solid ${r.csm ? J.edge : J.overdueEdge}`, color: r.csm ? J.ink : J.overdueInk }}>
                <option value="">Unassigned</option>
                {csmOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <div style={LABEL}>TARGET GO-LIVE</div>
              <input type="date" defaultValue={r.target_go_live || ""} data-testid="journey-panel-target"
                onChange={(e) => save({ target_go_live: e.target.value || null }, "the target date")}
                className={INPUT} style={{ border: `1px solid ${J.edge}` }} />
            </div>
            <div>
              <div style={LABEL}>WENT LIVE</div>
              <input type="date" defaultValue={r.went_live || ""} data-testid="journey-panel-went-live"
                onChange={(e) => save({ actual_go_live: e.target.value || null }, "the go-live date")}
                className={INPUT} style={{ border: `1px solid ${J.edge}` }} />
            </div>
            <div>
              <div style={LABEL}>IMPLEMENTATION HOURS</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="mono text-[13px] whitespace-nowrap" data-testid="journey-panel-hours-figure"
                  style={{ color: r.hours_allowance && shownHours > r.hours_allowance ? J.overdue : J.ink }}>
                  {Math.round(shownHours * 100) / 100}{r.hours_allowance ? ` / ${r.hours_allowance}h` : "h"}
                </span>
                {r.hours_allowance_is_override && (
                  <span className="text-[10px] font-bold uppercase" style={{ color: J.muted }}
                    title="Set for this client rather than the track default">set</span>
                )}
                <button onClick={() => setHoursOpen(true)} data-testid="journey-panel-edit-hours"
                  className="h-9 px-2.5 rounded-[7px] text-[12px] font-bold"
                  style={{ background: J.accent, border: `1px solid ${J.accentEdge}`, color: J.accentInk }}>
                  Hours
                </button>
              </div>
            </div>
          </div>

          {warns.length > 0 && (
            <div className="mt-4 rounded-[8px] p-3 space-y-1.5" data-testid="journey-panel-warnings"
              style={{ background: J.warnFill, border: `1px solid ${J.warnEdge}` }}>
              {warns.map((w, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[12.5px]" style={{ color: J.warnInk }}>
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {w}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {(r.stages || []).map((st, i) => {
            const list = byStage(st.key);
            const doneN = list.filter((s) => s.status === "done").length;
            const isCurrent = i + 1 === r.stage_index;
            const isOpen = openStage === st.key;
            return (
              <div key={st.key} className="rounded-[10px] bg-white" style={{ border: `1px solid ${J.edge}` }}
                data-testid={`journey-stage-${st.key}`}>
                <button onClick={() => setOpenStage(isOpen ? null : st.key)}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left"
                  data-testid={`journey-stage-toggle-${st.key}`}>
                  <span className="inline-flex items-center justify-center rounded-full shrink-0"
                    style={{ width: 22, height: 22, fontSize: 10.5, fontWeight: 700, ...MANROPE,
                      background: st.completed ? J.stageDone : isCurrent ? J.accent : "#fff",
                      border: st.completed || isCurrent ? "none" : `1px solid ${J.edge}`,
                      color: st.completed ? "#fff" : J.ink }}>
                    {st.completed ? <Check className="w-3 h-3" /> : i + 1}
                  </span>
                  <span className="text-[13.5px] font-bold flex-1 min-w-0 truncate" style={{ color: J.ink }}>{st.name}</span>
                  {isCurrent && <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", borderRadius: 3,
                    padding: "1px 5px", background: J.accent, color: J.accentInk }}>CURRENT</span>}
                  <span className="mono text-[11.5px]" style={{ color: J.muted }}>{doneN}/{list.length}</span>
                  {(isCurrent || st.completed) && (
                    <span onClick={(e) => { e.stopPropagation(); tickStage(st, !st.completed); }}
                      data-testid={`journey-stage-tick-${st.key}`} role="button"
                      title={st.completed ? "Reopen this stage" : "Mark this stage complete"}
                      className="mono text-[10px] font-bold rounded-[4px] px-1.5 py-0.5 whitespace-nowrap hover:opacity-80"
                      style={{ border: `1px solid ${J.edge}`, color: J.ink2, background: "#FBFAF7" }}>
                      {busy === `stage-${st.key}` ? "…" : st.completed ? "REOPEN" : "TICK OFF"}
                    </span>
                  )}
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5" style={{ color: J.muted }} />
                    : <ChevronRight className="w-3.5 h-3.5" style={{ color: J.muted }} />}
                </button>

                {isOpen && (list.length === 0 ? (
                  <div className="px-3.5 pb-3 text-[12.5px]" style={{ color: J.muted }}>
                    No steps yet — they appear once this stage is reached.
                  </div>
                ) : list.map((s) => {
                  const done = s.status === "done";
                  const planned = !!s.planned;
                  const d = dueLine(s.due_date, today);
                  return (
                    <div key={s.id} className="flex items-start gap-2.5 px-3.5 py-2.5"
                      style={{ borderTop: `1px solid ${J.rule}`, opacity: planned ? 0.62 : 1 }}
                      data-testid={`journey-step-${s.id}`}>
                      <button onClick={() => !planned && toggleStep(s)} disabled={planned}
                        data-testid={`journey-step-toggle-${s.id}`}
                        className="rounded-[4px] flex items-center justify-center shrink-0 mt-0.5"
                        style={{ width: 20, height: 20, background: done ? J.okSolid : "#fff",
                          borderStyle: planned ? "dashed" : "solid", borderWidth: 1.5,
                          borderColor: done ? J.okSolid : J.edge }}>
                        <Check className="w-3 h-3" style={{ color: done ? "#fff" : "transparent" }} />
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-1.5 flex-wrap">
                          <span className="text-[13px]" style={{ color: done ? J.muted : J.ink,
                            textDecoration: done ? "line-through" : "none" }}>{s.title}</span>
                          {s.required && !done && <span className="whitespace-nowrap" style={{ fontSize: 9,
                            fontWeight: 700, letterSpacing: "0.06em", borderRadius: 3, padding: "1px 4px",
                            background: J.overdueFill, color: J.overdueInk, border: `1px solid ${J.overdueEdge}` }}>REQUIRED</span>}
                        </div>
                        <div className="text-[11.5px]" style={{ color: J.muted }}>
                          {planned ? "Starts when this stage does"
                            : s.owner_type === "CLIENT_CONTACT" ? "Client contact"
                              : r.csm ? `CSM ${r.csm}` : "CSM · unassigned"}
                        </div>
                      </div>
                      <span className="mono text-[11.5px] shrink-0 text-right"
                        style={{ color: done || planned ? J.muted : d.color, fontWeight: !done && d.bold ? 600 : 400 }}>
                        {planned ? "Planned" : done ? (
                          <span className="inline-flex items-center gap-1" title="Date this step was completed">
                            Done ·
                            <input type="date" value={doneOn(s)} max={today}
                              onChange={(e) => setStepDate(s, e.target.value)}
                              data-testid={`journey-step-date-${s.id}`}
                              className="mono text-[11.5px] rounded-[5px] px-1 py-0.5 bg-white"
                              style={{ border: `1px solid ${J.edge}`, color: J.ink2, width: 116 }} />
                          </span>
                        ) : d.text}
                      </span>
                      {!planned && !s.required && (
                        <button onClick={() => removeStep(s)} disabled={busy === `rm-${s.id}`}
                          data-testid={`journey-step-remove-${s.id}`}
                          title="Remove this step — it doesn't apply to this client"
                          className="shrink-0 rounded-[5px] p-1 hover:bg-[#F5F2EA] disabled:opacity-40"
                          style={{ color: J.muted }}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                }))}
              </div>
            );
          })}
        </div>

        <div className="px-6 py-3.5 bg-white flex items-center gap-2" style={{ borderTop: `1px solid ${J.rule}` }}>
          <a href={`/clients/${r.client_id}`} data-testid="journey-panel-open-client"
            className="h-9 px-3 rounded-[7px] text-[12.5px] font-semibold inline-flex items-center gap-1.5 whitespace-nowrap"
            style={{ border: `1px solid ${J.edge}`, color: J.ink2 }}>
            <ExternalLink className="w-3.5 h-3.5" /> Open client record
          </a>
          <a href={`/company/to-dos?type=task&client=${encodeURIComponent(r.company_name || "")}`}
            data-testid="journey-panel-open-todos"
            className="h-9 px-3 rounded-[7px] text-[12.5px] font-semibold inline-flex items-center gap-1.5 whitespace-nowrap"
            style={{ border: `1px solid ${J.edge}`, color: J.ink2 }}>
            <ExternalLink className="w-3.5 h-3.5" /> Open in To-dos
          </a>
          <span className="text-[11.5px] ml-auto truncate" style={{ color: J.muted }}>Steps sync to To-dos</span>
        </div>
      </div>
    </div>
  );
};
